/**
 * m3 echo Frontend - Authentication Store
 *
 * Zustand store for managing authentication state.
 * Handles login, logout, token storage, and user/tenant data.
 *
 * Security improvements (v4.5.0):
 * - Access tokens stored in-memory only (NOT in localStorage)
 * - Refresh tokens stored in HttpOnly cookies (handled by browser)
 * - Access tokens valid for 60 minutes
 * - Automatic token refresh on expiry
 */

import { create } from 'zustand';
import type { User, Tenant, TokenResponse } from '../types/api';

interface AuthState {
  // State - all in-memory only
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;  // Renamed from 'token' for clarity
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;  // Renamed from isHydrated - indicates initial auth check done
  tokenExpiresAt: number | null;  // Unix timestamp when access token expires
  maintenanceMode: boolean;

  // Actions
  setAuth: (data: TokenResponse) => void;
  clearAuth: () => void;
  setUser: (user: User) => void;
  setTenant: (tenant: Tenant) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  updateMustChangePassword: (mustChange: boolean) => void;
  updateAccessToken: (token: string) => void;
  isTokenExpired: () => boolean;
  setMaintenanceMode: (mode: boolean) => void;
  updateOnboardingCompleted: (completed: boolean) => void;
  updateOnboardingStep: (step: number) => void;
}

// Calculate token expiry time (60 minutes from now)
const calculateTokenExpiry = (): number => {
  return Date.now() + (60 * 60 * 1000);  // 60 minutes in milliseconds
};

export const useAuthStore = create<AuthState>()(
  // NO persist middleware - tokens stay in-memory only
  (set, get) => ({
    // Initial state
    user: null,
    tenant: null,
    accessToken: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    tokenExpiresAt: null,
    maintenanceMode: false,

    // Set authentication data after login
    setAuth: (data: TokenResponse) => {
      set({
        user: data.user,
        tenant: data.tenant,
        accessToken: data.access_token,
        isAuthenticated: true,
        tokenExpiresAt: calculateTokenExpiry(),
        maintenanceMode: data.maintenance_mode ?? false,
      });
    },

    // Clear authentication data on logout
    clearAuth: () => {
      set({
        user: null,
        tenant: null,
        accessToken: null,
        isAuthenticated: false,
        tokenExpiresAt: null,
        maintenanceMode: false,
      });
    },

    // Update user data
    setUser: (user: User) => {
      set({ user });
    },

    // Update tenant data
    setTenant: (tenant: Tenant) => {
      set({ tenant });
    },

    // Set loading state
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    // Set initialized state (initial auth check completed)
    setInitialized: (initialized: boolean) => {
      set({ isInitialized: initialized });
    },

    // Update must_change_password flag
    updateMustChangePassword: (mustChange: boolean) => {
      set((state) => ({
        user: state.user
          ? { ...state.user, must_change_password: mustChange }
          : null,
      }));
    },

    // Update access token (used after refresh)
    updateAccessToken: (token: string) => {
      set({
        accessToken: token,
        tokenExpiresAt: calculateTokenExpiry(),
      });
    },

    // Check if access token is expired or about to expire (within 1 minute)
    isTokenExpired: () => {
      const { tokenExpiresAt } = get();
      if (!tokenExpiresAt) return true;
      // Consider expired if less than 1 minute remaining
      return Date.now() > (tokenExpiresAt - 60000);
    },

    // Set maintenance mode
    setMaintenanceMode: (mode: boolean) => {
      set({ maintenanceMode: mode });
    },

    // Update onboarding_completed flag locally
    updateOnboardingCompleted: (completed: boolean) => {
      set((state) => ({
        user: state.user
          ? { ...state.user, onboarding_completed: completed }
          : null,
      }));
    },

    // Update onboarding_step locally
    updateOnboardingStep: (step: number) => {
      set((state) => ({
        user: state.user
          ? { ...state.user, onboarding_step: step }
          : null,
      }));
    },
  })
);

/**
 * Stable cache scope of the currently authenticated identity.
 *
 * Used as a segment of tenant sensitive query keys so a leftover cache entry can
 * never be served across an account or tenant switch.
 */
export const getIdentityScope = (): string => {
  const { user, tenant } = useAuthStore.getState();
  if (!user) return 'anonymous';
  return `u${user.id}:t${tenant?.id ?? 'none'}`;
};

// Selector hooks for convenience
export const useUser = () => useAuthStore((state) => state.user);
export const useTenant = () => useAuthStore((state) => state.tenant);
export const useToken = () => useAuthStore((state) => state.accessToken);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useIsAdmin = () =>
  useAuthStore((state) => state.user?.is_admin || false);
export const useUserRole = () =>
  useAuthStore((state) => state.user?.role ?? 'viewer');
export const useCanEdit = () =>
  useAuthStore((state) => state.user?.is_admin || state.user?.role === 'editor');
export const useMustChangePassword = () =>
  useAuthStore((state) => state.user?.must_change_password || false);
export const useMaintenanceMode = () =>
  useAuthStore((state) => state.maintenanceMode);
export const useNeedsOnboarding = () =>
  useAuthStore((state) => (state.user ? !state.user.onboarding_completed : false));
export const useIdentityScope = () =>
  useAuthStore((state) =>
    state.user ? `u${state.user.id}:t${state.tenant?.id ?? 'none'}` : null
  );
